import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatDate, formatRelative } from "../lib/api";
import { fmtMoney } from "../components/ops/opsUtils";
import { FileSignature, CheckCircle2, Clock, Send, XCircle, Plus } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGES = {
  draft:        { bg: "#F3F4F6", text: "#4B5563", label: "Draft",        Icon: FileSignature },
  sent:         { bg: "#EFF6FF", text: "#1D4ED8", label: "Sent",         Icon: Send },
  viewed:       { bg: "#FEF3C7", text: "#B45309", label: "Viewed",       Icon: Clock },
  signed:       { bg: "#DCFCE7", text: "#166534", label: "Signed",       Icon: CheckCircle2 },
  completed:    { bg: "#DCFCE7", text: "#166534", label: "Completed",    Icon: CheckCircle2 },
  funding_sent: { bg: "#DBEAFE", text: "#1E40AF", label: "Funding Sent", Icon: CheckCircle2 },
  voided:       { bg: "#FEE2E2", text: "#B91C1C", label: "Voided",       Icon: XCircle },
};

export default function ContractsIndex() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/operations/contracts`);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const counts = {
    draft: rows.filter((r) => r.status === "draft").length,
    sent: rows.filter((r) => r.status === "sent" || r.status === "viewed").length,
    signed: rows.filter((r) => r.status === "signed" || r.status === "completed" || r.status === "funding_sent").length,
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="contracts-index">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
            <FileSignature className="w-3.5 h-3.5" /> Agreements
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Contracts</h1>
          <p className="text-sm text-[#6B7280] mt-1">Auto-generated, e-signed Nonrecourse Purchase Agreements per funding.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        <Stat label="Total agreements" value={rows.length} />
        <Stat label="Drafts" value={counts.draft} />
        <Stat label="Out for signature" value={counts.sent} accent />
        <Stat label="Signed" value={counts.signed} success />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <th className="px-4 py-2.5 font-medium">Number</th>
              <th className="px-4 py-2.5 font-medium">Client</th>
              <th className="px-4 py-2.5 font-medium">Attorney</th>
              <th className="px-4 py-2.5 font-medium">Loan</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Generated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6B7280]" data-testid="contracts-loading">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-16 text-center" data-testid="contracts-empty">
                <div className="inline-flex flex-col items-center text-[#6B7280]">
                  <div className="w-12 h-12 rounded-full bg-[#F0F6FF] flex items-center justify-center mb-3"><FileSignature className="w-5 h-5 text-[#0066FF]" /></div>
                  <div className="text-sm font-medium text-[#0A2540]">No agreements yet</div>
                  <div className="text-xs mt-0.5">Generate one from any loan detail page.</div>
                </div>
              </td></tr>
            ) : rows.map((r) => {
              const cfg = STATUS_BADGES[r.status] || STATUS_BADGES.draft;
              const Icon = cfg.Icon;
              return (
                <tr key={r.contract_id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`contract-row-${r.contract_id}`}>
                  <td className="px-4 py-3"><Link to={`/admin/contracts/${r.contract_id}`} className="text-[#0066FF] hover:underline tabular-nums">{r.contract_number || r.contract_id}</Link></td>
                  <td className="px-4 py-3 text-[#0A2540] font-medium"><Link to={`/admin/contracts/${r.contract_id}`}>{r.client_name}</Link></td>
                  <td className="px-4 py-3 text-[#6B7280] text-xs">{r.attorney_email}</td>
                  <td className="px-4 py-3 text-[#6B7280] tabular-nums"><Link to={`/admin/loans/${r.loan_id}`} className="hover:underline text-[#0066FF]">{r.loan_id}</Link></td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: cfg.bg, color: cfg.text }}><Icon className="w-3 h-3" /> {cfg.label}</span></td>
                  <td className="px-4 py-3 text-[#6B7280] text-xs tabular-nums">{formatRelative(r.generated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, success }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">{label}</div>
      <div className={`tabular-nums font-display text-2xl font-semibold ${success ? "text-[#166534]" : accent ? "text-[#0066FF]" : "text-[#0A2540]"}`}>{value}</div>
    </div>
  );
}
